import React from "react";
import { VideoOff, X, Check, ArrowRight } from 'lucide-react';
import { leftTopArrowGradient, rightBottomArrowGradient } from "@/assets/svgs/gradientIcons";

import { TwinneeLogo } from '@/components/TwinneeLogo';
export const ComparisonSection = () => {
  const oldWayPoints = [
    "Costs thousands, booked weeks in advance",
    "Depends on someone else's schedule",
    "Editing takes days or weeks to get back",
    "Limited to what they filmed on the day",
  ];
  const twinneeWayPoints = [
    "Starts free, no waiting in line",
    "Make it whenever it suits you, any day",
    "Ready to watch in minutes",
    "Try as many styles as you like, by tapping",
  ];

  return (
    <section id="comparison" className="text-white py-24 px-4 sm:px-6 lg:px-8 font-sora relative">
      <div className="absolute -top-32 -left-32 w-[600px] md:w-[900px] h-auto pointer-events-none z-0 opacity-70" dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }} />
      <div className="absolute -bottom-48 right-0 w-[550px] md:w-[750px] h-auto pointer-events-none z-0 opacity-60" dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }} />

      <div className="max-w-6xl mx-auto text-center relative z-10">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">WHY USERS CHOOSE TWINNEE</p>
        <h2 className="text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.18]">
          The Old Way <span className="text-white font-normal">vs.</span>{" "}
          <span className="text-[#62FAE3]">The</span>
          <br />
          <span className="text-[#62FAE3]">Twinnee Way</span>
        </h2>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 text-left max-w-5xl mx-auto">
          {/* Old Way */}
          <div className="relative rounded-[28px] border border-white/10 bg-[#131418]/90 backdrop-blur-xl p-8 sm:p-10 flex flex-col justify-between min-h-[460px] shadow-2xl">
            <div>
              <div className="flex items-center gap-3.5 mb-8">
                <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
                  <VideoOff className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-xl sm:text-2xl font-medium text-white tracking-tight">Hiring a videographer</h3>
              </div>
              <div className="divide-y divide-white/5">
                {oldWayPoints.map((point, idx) => (
                  <div key={idx} className="flex items-center gap-4 py-5 first:pt-0 last:pb-0">
                    <X className="w-4 h-4 text-gray-400 shrink-0 stroke-[2]" />
                    <span className="text-gray-400 text-sm sm:text-base font-light">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Twinnee Way */}
          <div className="relative rounded-[28px] border border-[#62FAE3]/30 bg-[#0c151a]/95 backdrop-blur-xl p-8 sm:p-10 flex flex-col justify-between min-h-[460px] shadow-2xl shadow-[#62FAE3]/10 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#62FAE3]/15 rounded-full blur-3xl pointer-events-none z-0" />
            <div className="relative z-10">
              <div className="flex items-center gap-3.5 mb-8">
                <div className="w-10 h-10 rounded-xl bg-[#62FAE3]/10 border border-[#62FAE3]/25 flex items-center justify-center shrink-0">
                  <TwinneeLogo className="w-5 h-5 text-[#62FAE3]" />
                </div>
                <h3 className="text-xl sm:text-2xl font-medium text-white tracking-tight">Making it with Twinnee</h3>
              </div>
              <div className="divide-y divide-white/5">
                {twinneeWayPoints.map((point, idx) => (
                  <div key={idx} className="flex items-center gap-4 py-5 first:pt-0 last:pb-0">
                    <div className="w-5 h-5 rounded-full bg-[#62FAE3] flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-[#0c151a] stroke-[3]" />
                    </div>
                    <span className="text-gray-200 text-sm sm:text-base font-light">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative z-10 mt-8">
              <a href="#pricing" className="w-full py-4 px-6 rounded-2xl font-bold text-sm sm:text-base flex items-center justify-center gap-2 transition-all duration-300 bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] text-[#120a28] hover:brightness-110 shadow-lg shadow-[#62FAE3]/20 active:scale-[0.99] cursor-pointer">
                <span>Start Creating Now</span>
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Feature Image Cards */}
        <div className="mt-14 flex flex-col lg:flex-row gap-6 max-w-5xl mx-auto items-stretch">
          <div className="flex-1 relative rounded-[28px] overflow-hidden border border-white/10 bg-[#0d1017] shadow-2xl group transition-all duration-300 hover:border-white/20 h-[300px] sm:h-[350px] lg:h-[360px]">
            <img src={`${process.env.PUBLIC_URL || ""}/LandingPageAssets/pro-level-result-in-seconds.png`} alt="Pro-level results in seconds" className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05070a] via-[#05070a]/40 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 z-10 text-left">
              <h4 className="text-white text-lg sm:text-xl font-semibold tracking-tight mb-1">Pro-level results in seconds</h4>
              <p className="text-gray-300 text-xs sm:text-sm font-light leading-relaxed">Advanced AI handles the complex editing for you.</p>
            </div>
          </div>
          <div className="w-full lg:w-[320px] shrink-0 relative rounded-[28px] overflow-hidden border border-white/10 bg-[#0d1017] shadow-2xl group transition-all duration-300 hover:border-white/20 h-[300px] sm:h-[350px] lg:h-[360px]">
            <img src={`${process.env.PUBLIC_URL || ""}/LandingPageAssets/creative-freedom.png`} alt="Creative Freedom" className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05070a] via-[#05070a]/40 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 z-10 text-left">
              <h4 className="text-white text-lg sm:text-xl font-semibold tracking-tight mb-1">Creative Freedom</h4>
              <p className="text-gray-300 text-xs sm:text-sm font-light leading-relaxed">Limitless styles at your fingertips.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
